// Public Payment Page — pay a tokenised Stripe payment link (no auth required)
import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, ShieldCheck, CreditCard, XCircle } from "lucide-react";

export default function PublicPayPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const [payState, setPayState] = useState("idle"); // idle | checking | paid | expired

  const fetchLink = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/payments/links/${token}`);
      setLink(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "This payment link could not be found.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const pollStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    setPayState("checking");
    try {
      const res = await axios.get(`${API_URL}/payments/link/status/${sid}`);
      if (res.data.payment_status === "paid") {
        setPayState("paid");
        return;
      }
      if (res.data.status === "expired") {
        setPayState("expired");
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(() => pollStatus(sid, attempts + 1), 2000);
      } else {
        setPayState("idle");
        toast.info("Still processing — please check your email for confirmation.");
      }
    } catch (e) {
      if (attempts < maxAttempts) {
        setTimeout(() => pollStatus(sid, attempts + 1), 2000);
      } else {
        setPayState("idle");
        toast.error("Could not verify payment status.");
      }
    }
  }, []);

  useEffect(() => {
    fetchLink();
  }, [fetchLink]);

  useEffect(() => {
    if (sessionId) pollStatus(sessionId);
  }, [sessionId, pollStatus]);

  const handlePay = async () => {
    setRedirecting(true);
    try {
      const res = await axios.post(`${API_URL}/payments/links/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the payment.");
      setRedirecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        data-testid="public-pay-card"
      >
        <div className="bg-gradient-to-br from-sky-700 to-indigo-800 px-7 py-6 text-white">
          <div className="flex items-center gap-2 text-sky-100 text-sm font-medium">
            <ShieldCheck className="h-4 w-4" /> Europacific Group · Secure payment
          </div>
          <p className="mt-1 text-xs text-sky-200/80">Powered by Stripe</p>
        </div>

        <div className="px-7 py-7">
          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-6" data-testid="public-pay-error">
              <XCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-slate-800">Link unavailable</h2>
              <p className="text-sm text-slate-500 mt-1">{error}</p>
            </div>
          )}

          {!loading && link && payState === "paid" && (
            <div className="text-center py-6" data-testid="public-pay-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-slate-800">Payment received</h2>
              <p className="text-sm text-slate-500 mt-1">
                Thank you. A confirmation has been recorded for{" "}
                <span className="font-medium">{link.description}</span>.
              </p>
            </div>
          )}

          {!loading && link && payState !== "paid" && (
            <>
              {link.status === "paid" ? (
                <div className="text-center py-6" data-testid="public-pay-already">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <h2 className="text-lg font-semibold text-slate-800">Already paid</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    This payment has already been completed.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500">Amount due</p>
                  <p
                    className="text-4xl font-bold text-slate-900 tracking-tight"
                    data-testid="public-pay-amount"
                  >
                    {link.currency === "USD" ? "$" : "€"}
                    {Number(link.amount).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    <span className="text-base font-medium text-slate-400">{link.currency}</span>
                  </p>
                  <p className="mt-3 text-sm text-slate-600">{link.description}</p>
                  {link.pf_number && (
                    <p className="mt-1 text-xs text-slate-400">Ref: {link.pf_number}</p>
                  )}

                  <Button
                    onClick={handlePay}
                    disabled={redirecting || payState === "checking"}
                    className="mt-6 w-full h-12 text-base bg-indigo-700 hover:bg-indigo-800"
                    data-testid="public-pay-button"
                  >
                    {redirecting || payState === "checking" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {payState === "checking" ? "Verifying…" : "Redirecting to Stripe…"}
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" /> Pay now
                      </>
                    )}
                  </Button>
                  {payState === "expired" && (
                    <p className="mt-3 text-center text-sm text-rose-500">
                      The previous session expired. Tap Pay now to try again.
                    </p>
                  )}
                  <p className="mt-4 text-center text-xs text-slate-400">
                    You'll be redirected to Stripe's secure checkout.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
